import { ACTIONS } from '~publish/legacy/ai/components/AIAssistant/AIAssistant.constants'
import { getContentGenerationMutation } from '~publish/legacy/ai/state/mutations'
import { client } from '~publish/legacy/apollo-client'

type ContentGenerationResponse = Record<
  string,
  { suggestions?: { content: string }[]; code?: string }
>

/**
 *
 */
export async function getGeneratedText({
  organizationId,
  prompt,
  previousGeneration,
  isHighlighted,
  action,
  service,
  metadata,
}: {
  organizationId: string
  prompt: string
  previousGeneration?: string | null
  isHighlighted: boolean
  action: string
  service?: string
  metadata: { placement: string; retry: boolean }
}): Promise<string> {
  const { placement, retry } = metadata

  let mutationName = action
  if (
    !Object.values(ACTIONS).find(
      (actionDefinition) => actionDefinition.mutation === mutationName,
    )
  ) {
    mutationName = 'generateText'
  }

  let generatedContent: string | undefined

  const status = retry ? 'Retry' : 'First'

  const cta = `publish-${placement}-${action}-${status}-${
    isHighlighted ? 'Highlighted-' : ''
  }1`

  await client
    .mutate<
      ContentGenerationResponse,
      {
        organizationId: string
        config: {
          prompt: string
          service?: string
          cta: string
          previousGeneration?: string
        }
      }
    >({
      mutation: getContentGenerationMutation(mutationName),
      variables: {
        organizationId,
        config: {
          prompt,
          service,
          cta,
          previousGeneration: previousGeneration || undefined,
        },
      },
    })
    .then((response) => {
      // handles a returned error
      if (response.data?.[action]?.code) {
        const errorMessage =
          response.data?.[action]?.code === 'InsuffiecientTokens'
            ? "Whoops! Looks like you don't have any tokens left."
            : 'Whoops! We are unable to generate content at this time. Please try again.'
        throw new Error(errorMessage)
      } else {
        // handles a successful response
        const generatedText = response.data?.[action]?.suggestions

        /**
         * for now we are just returning 1 object with a content property
         * but when we return more complex types, we will need to handle it
         * differently */
        generatedContent = generatedText?.[0]?.content
        if (!generatedContent) {
          throw new Error(
            'Whoops! We are unable to generate content at this time. Please try again.',
          )
        }
        return generatedContent
      }
    })
    .catch(() => {
      const errorMessage =
        'Whoops! We are unable to generate content at this time. Please try again.'

      throw new Error(errorMessage)
    })
  if (!generatedContent) {
    throw new Error(
      'Whoops! We are unable to generate content at this time. Please try again.',
    )
  }

  return generatedContent
}
